import React from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { ConversationSummary, HistoryErrorKind } from './lib/copilotTypes'
import { ChatBubbleIcon, BackIcon, PlusIcon, TrashIcon } from './icons'
import { cx } from './cx'
import styles from './index.module.scss'

interface HistoryDrawerProps {
  open: boolean
  sessions: ConversationSummary[]
  activeId: string | null
  loading: boolean
  error: HistoryErrorKind | null
  onSelect: (sessionId: string) => void
  onNew: () => void
  onDelete: (sessionId: string) => void
  onClose: () => void
}

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  open,
  sessions,
  activeId,
  loading,
  error,
  onSelect,
  onNew,
  onDelete,
  onClose
}) => {
  const { t } = useTranslation()

  const handleDelete = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    // eslint-disable-next-line no-alert
    if (window.confirm(t('genAI.copilot-delete-confirm'))) onDelete(sessionId)
  }

  const showEmpty = !loading && !error && sessions.length === 0

  return (
    <div className={cx(styles.history, open && styles.historyOpen)}>
      <div className={styles.historyHead}>
        {t('genAI.copilot-conversations')}
        <button
          className={styles.iconBtn}
          onClick={onClose}
          title={t('genAI.copilot-back')}
          aria-label={t('genAI.copilot-back')}>
          <BackIcon />
        </button>
      </div>
      <button className={styles.historyNew} onClick={onNew}>
        <PlusIcon />
        {t('genAI.copilot-new-chat')}
      </button>
      <div className={styles.historyList}>
        {loading && <div className={styles.historyEmpty}>{t('genAI.copilot-history-loading')}</div>}
        {error && <div className={styles.historyErr}>{t('genAI.copilot-history-error')}</div>}
        {showEmpty && <div className={styles.historyEmpty}>{t('genAI.copilot-no-history')}</div>}
        {!error &&
          sessions.map((s) => {
            const title = (s.title || '').trim() || t('genAI.copilot-new-conversation')
            const count = typeof s.message_count === 'number' && s.message_count > 0 ? s.message_count : null
            const when = s.updated_at ? moment(s.updated_at).fromNow() : moment().fromNow()
            return (
              <div key={s.session_id} className={cx(styles.historyItem, s.session_id === activeId && styles.active)}>
                <button className={styles.hiSelect} onClick={() => onSelect(s.session_id)}>
                  <span className={styles.hiIco}>
                    <ChatBubbleIcon />
                  </span>
                  <span className={styles.hiMain}>
                    <span className={styles.hiTitle}>{title}</span>
                    {s.last_message_preview && <span className={styles.hiSnip}>{s.last_message_preview}</span>}
                    <span className={styles.hiSnip}>
                      {when}
                      {count !== null && ` · ${t('genAI.copilot-messages-count', { n: count })}`}
                    </span>
                  </span>
                </button>
                <button
                  className={styles.hiDelete}
                  onClick={(e) => handleDelete(e, s.session_id)}
                  title={t('genAI.copilot-delete')}
                  aria-label={t('genAI.copilot-delete')}>
                  <TrashIcon />
                </button>
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default HistoryDrawer
